// Transformation-graph model for a recipe's ingredient list.
//
// The cook's mental model isn't a static shopping list — it's "what is
// physically in front of me right now." Raw ingredients get whisked
// into a "Wet mixture," dry goods get sifted into a "Dry mixture,"
// then the two fold into a "Batter." Once you've built a mixture, the
// things that went into it should stop appearing as separate items —
// they've been consumed. The list should always reflect the current
// physical state of the counter.
//
// This module derives that live state from three inputs, all of which
// already exist in CookMode:
//   1. the effective recipe (ingredients + steps, swaps/skips applied)
//   2. completedSteps  — which steps the cook has finished (a Set)
//   3. the cook session — per-ingredient "added to the bowl" flags
//
// It is a PURE DERIVATION (same contract as effectiveRecipe.js): never
// stored, re-run per render, source of truth stays (recipe, session).
//
// ─── The `combine` annotation ────────────────────────────────────────
// A step opts into the collapse by carrying a `combine` block:
//
//   step.combine = {
//     id:    "wet_mix",                  // stable id for the product
//     label: "Wet mixture",              // display name
//     from:  ["milk", "eggs", "oil"],    // ingredient names/ids AND/OR
//                                        //   prior intermediate ids it
//                                        //   consumes
//   }
//
// When that step is completed, every `from` entry that matches a raw
// ingredient marks it consumed, every entry that matches a previously
// produced intermediate consumes that too, and a new intermediate
// (id/label) appears on the counter. Steps without `combine` change
// nothing — recipes that were never annotated simply never collapse,
// which degrades gracefully to a flat (but wet/dry-grouped) list.

import { findIngredient, inferCanonicalFromNameLongestMatch } from "../data/ingredients";

// ─── Wet / dry classification ────────────────────────────────────────
// TWO layers, applied in this order by deriveCounterComponents():
//
//   A. CONTEXT (inferIngredientPhases, below) — what the recipe DOES
//      with the ingredient. Creaming butter + sugar → both wet, even
//      though sugar is "dry" by identity. This is the source of truth
//      when the steps make the placement clear.
//   B. IDENTITY (classifyWetDry, here) — the per-ingredient fallback
//      for anything the steps don't place. Order of signals:
//        1. canonical measuredIn.metric === "ml" → liquid volume.
//        2. canonical parentId in a known liquid hub.
//        3. keyword match on the ingredient id / display text
//           (fats — butter / ghee / shortening — read wet here).
//        4. default → dry.
//
// All canonical reads go through findIngredient (the sanctioned
// resolver) so synthetic + DB-overlaid canonicals classify too.

const WET_HUBS = new Set([
  "milk_hub", "cream_hub", "stock_hub", "broth_hub", "oil_hub",
  "juice_hub", "wine_hub", "vinegar_hub", "syrup_hub", "sauce_hub",
]);

// Liquids and wet-batter components. Deliberately broad on the things a
// home baker/cook folds into a "wet" bowl: eggs, dairy, oils, syrups,
// extracts, stocks, anything pourable.
const WET_RE = new RegExp(
  [
    "water", "stock", "broth", "consomm", "bouillon",
    "milk", "buttermilk", "cream", "half.and.half", "yogurt",
    "egg", "yolk", "egg white",
    // Fats read as wet in the baking / mise sense — creamed or melted
    // they fold into the wet bowl. Butter is word-bounded so
    // "butternut" / "butterscotch" don't false-match; nut-butters and
    // butter lettuce are excluded by WET_OVERRIDE_DRY_RE below.
    "\\bbutter\\b", "ghee", "shortening", "margarine",
    "oil", "wine", "beer", "sake", "mirin", "vermouth",
    "vinegar", "juice", "lemon juice", "lime juice",
    "soy sauce", "fish sauce", "worcestershire", "tamari",
    "honey", "syrup", "molasses", "agave", "nectar",
    "extract", "vanilla", "puree", "passata", "sauce",
    "broth", "wash", "brine", "marinade", "glaze", "slurry",
  ].join("|"),
  "i"
);

// Things that read as "wet" by keyword but are functionally dry/solid
// in mise-en-place terms — keep them out of the wet bowl.
const WET_OVERRIDE_DRY_RE = /(powder|dried|flake|granul|cube|bouillon cube|cornstarch|peanut butter|almond butter|cashew butter|sunflower butter|nut butter|cookie butter|apple butter|butter lettuce|butterhead)/i;

// STATE makes a normally-SOLID ingredient wet: melted chocolate or
// browned butter pours into the wet bowl even though the base canonical
// reads solid. (Butter itself now classifies wet by keyword — a fat that
// gets creamed or melted — so this mainly catches other melted solids
// like chocolate.) Tight on purpose — only states that genuinely
// liquefy the ingredient.
const WET_STATE_RE = /\b(melted|liquefied|browned butter)\b/i;

const VOLUME_METRIC = new Set(["ml", "l", "liter", "litre"]);

// Classify a single recipe-ingredient row. `ing` is the effective
// ingredient shape: { amount, item, ingredientId?, state? }.
// Returns "wet" | "dry".
export function classifyWetDry(ing) {
  // Include `state` so "melted" / "browned" (which ride on the state axis,
  // not the canonical) reach the wet-state check below.
  const text = `${ing?.item || ""} ${ing?.ingredientId || ""} ${ing?.state || ""}`.trim();

  // Keyword-level dry override first (so "garlic powder" / "dried
  // oregano" never land in the wet bowl on a loose "powder" → dry rule).
  if (WET_OVERRIDE_DRY_RE.test(text)) return "dry";

  // A liquefying STATE wins next — melted butter, browned butter, melted
  // chocolate pour into the wet bowl even though the base canonical is solid.
  if (WET_STATE_RE.test(text)) return "wet";

  const canonical = ing?.ingredientId ? findIngredient(ing.ingredientId) : null;
  if (canonical) {
    const measuredMetric = canonical.measuredIn?.metric;
    if (measuredMetric && VOLUME_METRIC.has(String(measuredMetric).toLowerCase())) {
      return "wet";
    }
    if (canonical.parentId && WET_HUBS.has(canonical.parentId)) return "wet";
  }

  if (WET_RE.test(text)) return "wet";
  return "dry";
}

// ─── Contextual mixture inference ────────────────────────────────────
// classifyWetDry() answers "what is this ingredient, in the abstract."
// But the bowl an ingredient lands in is decided by what the recipe DOES
// with it: creaming butter + sugar puts BOTH in the wet / creamed base
// even though sugar is "dry" by identity; sifting flour + leavener +
// salt forms the dry mix. So we read each step's technique, classify the
// step wet-forming / dry-forming, and tag every ingredient that step
// incorporates. First clear placement wins (the moment the ingredient
// enters a bowl). Ingredients no step places fall back to classifyWetDry.

// Verbs that build a WET / creamed / emulsified base.
const WET_VERB_RE = /\b(cream|creamed|creaming|beat|beaten|beating|whip|whipped|whipping|melt|melted|melting|dissolv\w+|emulsif\w+|mount\w*)\b/i;
// "whisk in the wet" style — a mixing verb aimed at a clearly liquid
// target. Kept separate so "whisk together the flour" doesn't read wet.
const WET_TARGET_RE = /\b(?:whisk|stir|mix|fold|blend|pour|add)\b[^.]{0,30}\b(?:wet|eggs?|milk|buttermilk|oil|cream|liquids?)\b/i;
const WET_MIX_NOUN_RE = /\bwet\s+(?:ingredient|mixture|mix)/i;
// Phrases that build the DRY mix.
const DRY_MIX_NOUN_RE = /\bdry\s+(?:ingredient|mixture|mix)/i;
const DRY_FORMING_RE = /\b(?:sift|sifted|sifting)\b|\b(?:whisk|stir|combine|mix|blend)\b[^.]{0,30}\bdry\b|\b(?:whisk|stir|combine)\b[^.]{0,24}\bflour\b/i;

// Classify one step's technique → "wet" | "dry" | null (ambiguous).
function inferStepPhase(text) {
  const t = String(text || "");
  const wet = WET_VERB_RE.test(t) || WET_TARGET_RE.test(t) || WET_MIX_NOUN_RE.test(t);
  const dry = DRY_MIX_NOUN_RE.test(t) || DRY_FORMING_RE.test(t);
  if (wet && !dry) return "wet";
  if (dry && !wet) return "dry";
  return null;
}

// An explicit combine annotation names a phase precisely (AI-authored).
// "batter" / "dough" are MERGED products (wet+dry), not a single bowl —
// they return null so prose inference (or the per-ingredient identity
// fallback) decides instead.
function combineBlockPhase(combine) {
  if (!combine) return null;
  const key = `${combine.id || ""} ${combine.label || ""}`.toLowerCase();
  if (/\bdry\b/.test(key)) return "dry";
  if (/\b(wet|cream|creamed|egg|eggs|liquid)\b/.test(key)) return "wet";
  return null;
}

// Qualifier tokens that describe HOW an ingredient is sold/prepped but
// aren't its identity — stripped before matching against step prose.
const ING_QUALIFIER_RE = /\b(unsalted|salted|granulated|packed|light|dark|brown|white|all|purpose|fresh|large|small|medium|whole|ground|fine|coarse|extra|virgin|pure|cold|warm|hot|room|temperature|softened|melted|sifted|chopped|diced|minced|grated|shredded|toasted|chilled|of|the|an|and|or|to|for|plus|more|optional|divided|heaping|level|ripe|raw|cooked|dry|dried)\b/g;

// The distinctive noun tokens of an ingredient ("granulated sugar" →
// ["sugar"], "all-purpose flour" → ["flour"], "baking soda" →
// ["baking","soda"]) used to detect whether a step mentions it.
function ingredientMatchTokens(ing) {
  const raw = `${ing?.item || ""} ${ing?.ingredientId || ""}`
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(ING_QUALIFIER_RE, " ")
    .replace(/[^a-z0-9 ]+/g, " ");
  const out = [];
  const seen = new Set();
  for (const w of raw.split(/\s+/)) {
    if (w.length >= 3 && !seen.has(w)) { seen.add(w); out.push(w); }
  }
  return out;
}

function stepMentionsTokens(instrLower, tokens) {
  return tokens.some((tok) => new RegExp(`\\b${tok}\\b`).test(instrLower));
}

// Walk the steps in order; return a Map of ingredient index → "wet" |
// "dry" for every ingredient a step clearly places into a bowl.
export function inferIngredientPhases(recipe) {
  const ingredients = Array.isArray(recipe?.ingredients) ? recipe.ingredients : [];
  const steps = Array.isArray(recipe?.steps) ? recipe.steps : [];
  const phases = new Map();
  if (!ingredients.length || !steps.length) return phases;
  const tokensByIdx = ingredients.map(ingredientMatchTokens);

  for (const step of steps) {
    const combinePhase = combineBlockPhase(step?.combine);
    const fromList = Array.isArray(step?.combine?.from) ? step.combine.from : [];

    // Precise path: an AI-authored combine with a wet/dry phase places
    // exactly the ingredients it names.
    if (combinePhase && fromList.length) {
      for (const token of fromList) {
        const idx = ingredients.findIndex(
          (ing, i) => !phases.has(i) && fromMatchesIngredient(token, ing),
        );
        if (idx >= 0) phases.set(idx, combinePhase);
      }
      continue;
    }

    // Inferred path: read the step technique and tag the raw ingredients
    // it mentions.
    const phase = inferStepPhase(`${step?.title || ""}. ${step?.instruction || ""}`);
    if (!phase) continue;
    const instrLower = `${step?.title || ""} ${step?.instruction || ""}`.toLowerCase();
    ingredients.forEach((ing, idx) => {
      if (phases.has(idx)) return;          // first clear placement wins
      const toks = tokensByIdx[idx];
      if (toks.length && stepMentionsTokens(instrLower, toks)) phases.set(idx, phase);
    });
  }
  return phases;
}

// ─── Live counter derivation ─────────────────────────────────────────
// Normalize a string for loose matching between a step.combine `from`
// entry and an ingredient's item/ingredientId.
function norm(s) {
  return String(s || "")
    .toLowerCase()
    .replace(/\(.*?\)/g, " ")     // drop parentheticals "(from prev step)"
    .replace(/[^a-z0-9 ]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

// Does a combine `from` token refer to this ingredient row?
function fromMatchesIngredient(fromToken, ing) {
  const t = norm(fromToken);
  if (!t) return false;
  const candidates = [
    ing.ingredientId,
    ing.item,
    ing._swappedFrom?.item,
    ing._swappedFrom?.ingredientId,
  ].filter(Boolean).map(norm);
  if (candidates.includes(t)) return true;
  // Loose containment both directions ("milk" ⊆ "whole milk",
  // "whole milk" ⊇ "milk") so author/AI tokens don't have to be exact.
  if (candidates.some((c) => c && (c.includes(t) || t.includes(c)))) return true;
  // WORD-SUBSET tier — contiguous substring fails exactly where
  // pantry pairing renames rows: token "dark brown cane sugar" vs
  // row "Brown Sugar" ("brown sugar" is not contiguous inside
  // "dark brown CANE sugar"). If every word of the shorter name
  // appears in the longer's word set, that's the same ingredient.
  // (This was the reported bug: brown sugar + a branded EVOO fell
  // out of the "Cinnamon brown sugar coating" group into WET/DRY.)
  const tWords = t.split(" ").filter(Boolean);
  for (const c of candidates) {
    const cWords = c.split(" ").filter(Boolean);
    if (!tWords.length || !cWords.length) continue;
    const [shorter, longer] = tWords.length <= cWords.length
      ? [tWords, cWords] : [cWords, tWords];
    if (shorter.every((w) => longer.includes(w))) return true;
  }
  // CANONICAL tier — brand-named tokens ("SIZZLE EVOO") share zero
  // words with the paired row ("Olive Oil"), but the alias map
  // resolves both to the same canonical. Sanctioned resolver route.
  if (ing.ingredientId) {
    try {
      const resolved = inferCanonicalFromNameLongestMatch(fromToken);
      if (resolved && resolved === ing.ingredientId) return true;
    } catch { /* resolver unavailable — fall through */ }
  }
  return false;
}

// Derive the components physically present on the counter right now.
//
// Returns:
//   {
//     intermediates: [{ type:"intermediate", id, label, stepIdx }],
//     groups: {
//       wet: [{ type:"ingredient", idx, ing, group:"wet", added }],
//       dry: [...],
//     },
//     hasCombines: boolean,   // did this recipe annotate any combine?
//     allPresentCount: number,
//   }
//
// `idx` is the index into recipe.ingredients (== session.overrides key).
export function deriveCounterComponents(recipe, {
  completedSteps = new Set(),
  isAdded = () => false,
} = {}) {
  const ingredients = Array.isArray(recipe?.ingredients) ? recipe.ingredients : [];
  const steps = Array.isArray(recipe?.steps) ? recipe.steps : [];

  const consumedIdx = new Set();          // raw ingredient indices used up
  const consumedIntermediate = new Set(); // intermediate ids used up
  const producedOrder = [];               // [{ id, label, stepIdx }]
  const producedById = new Map();
  let hasCombines = false;

  // Walk steps in order; a completed combine-step consumes its inputs
  // and emits its product. Process in step order so a later step can
  // consume an intermediate an earlier step produced.
  steps.forEach((step, stepIdx) => {
    const c = step?.combine;
    if (!c || !c.id) return;
    hasCombines = true;
    if (!completedSteps.has(stepIdx)) return;

    const fromList = Array.isArray(c.from) ? c.from : [];
    for (const token of fromList) {
      // Intermediate id match wins (explicit id reference).
      if (producedById.has(token)) {
        consumedIntermediate.add(token);
        continue;
      }
      // Otherwise bind to a raw ingredient row.
      const hitIdx = ingredients.findIndex(
        (ing, i) => !consumedIdx.has(i) && fromMatchesIngredient(token, ing)
      );
      if (hitIdx >= 0) consumedIdx.add(hitIdx);
    }

    // Emit the product (unless an identically-id'd one already exists).
    if (!producedById.has(c.id)) {
      const node = { id: c.id, label: c.label || c.id, stepIdx };
      producedById.set(c.id, node);
      producedOrder.push(node);
    }
  });

  // Present intermediates = produced and not yet consumed downstream.
  const intermediates = producedOrder
    .filter((n) => !consumedIntermediate.has(n.id))
    .map((n) => ({ type: "intermediate", ...n }));

  // NAMED pending mixtures — the recipe's OWN grouping. A not-yet-
  // completed combine step claims its raw inputs as a named section
  // ("Dry rub mixture", "Garlic herb crust") so the counter mirrors
  // the recipe's plan instead of force-fitting everything into
  // Wet/Dry (the reported bug: a recipe declares dry_rub_mixture and
  // the reader still rendered WET / DRY headers — with the chicken
  // under DRY). First claim wins per ingredient; rows no pending
  // combine names fall back to the wet/dry phases below.
  const allCombineIds = new Set(
    steps.map((s) => s?.combine?.id).filter(Boolean),
  );
  const pendingCombineByIdx = new Map(); // ingredientIdx → { id, label }
  steps.forEach((step, stepIdx) => {
    const c = step?.combine;
    if (!c || !c.id || completedSteps.has(stepIdx)) return;
    const fromList = Array.isArray(c.from) ? c.from : [];
    for (const token of fromList) {
      // A `from` entry can reference another combine's product —
      // that's an intermediate handoff, not a raw ingredient claim.
      if (allCombineIds.has(token)) continue;
      const hitIdx = ingredients.findIndex(
        (ing, i) =>
          !consumedIdx.has(i) &&
          !pendingCombineByIdx.has(i) &&
          fromMatchesIngredient(token, ing),
      );
      if (hitIdx >= 0) {
        pendingCombineByIdx.set(hitIdx, { id: c.id, label: c.label || c.id });
      }
    }
  });

  // Present raw ingredients = not consumed by any completed combine.
  // Section membership: the recipe's named combine first, then the
  // CONTEXTUAL wet/dry phase (which bowl a step places the ingredient
  // into), then the per-ingredient identity guess.
  const phases = inferIngredientPhases(recipe);
  const wet = [];
  const dry = [];
  const namedById = new Map(); // combineId → { id, label, rows }
  const namedOrder = [];
  ingredients.forEach((ing, idx) => {
    if (consumedIdx.has(idx)) return;
    if (ing?._skipped) return;            // user dropped this row
    const group = phases.get(idx) || classifyWetDry(ing);
    const row = { type: "ingredient", idx, ing, group, added: !!isAdded(idx) };
    const named = pendingCombineByIdx.get(idx);
    if (named) {
      let bucket = namedById.get(named.id);
      if (!bucket) {
        bucket = { id: named.id, label: named.label, rows: [] };
        namedById.set(named.id, bucket);
        namedOrder.push(bucket);
      }
      bucket.rows.push(row);
    } else {
      (group === "wet" ? wet : dry).push(row);
    }
  });

  const namedCount = namedOrder.reduce((n, s) => n + s.rows.length, 0);

  return {
    intermediates,
    groups: { wet, dry },
    // Ordered render sections: the recipe's named mixtures first (in
    // step order), then the wet/dry leftovers. CounterList prefers
    // this when any named section exists; `groups` stays for callers
    // that only know wet/dry.
    sections: [
      ...namedOrder,
      ...(wet.length ? [{ id: "wet", label: "Wet", rows: wet }] : []),
      ...(dry.length ? [{ id: "dry", label: "Dry", rows: dry }] : []),
    ],
    hasCombines,
    allPresentCount: wet.length + dry.length + namedCount + intermediates.length,
  };
}
